import "./global.css";
import { PropsWithChildren, useEffect, useRef } from "react";
import {
  Route,
  Routes,
  Navigate,
  RouterProvider,
  createBrowserRouter,
  ScrollRestoration,
} from "react-router-dom";
import { Navbar } from "./components/Navbar";
import ContentReviewer from "./views/ContentReviewer";
import { TokenAgentsPage } from "./views/manage_personas/TokenAgentsPage.tsx";
import NotFoundPage from "./views/NotFoundPage.jsx";
import AuthorizationProvider, {
  Role,
} from "@/components/AuthorizationProvider";
import PrivateRoute from "./components/PrivateRoute";
import RestrictedRoute from "./components/RestrictedRoute";
import Admin from "./views/Admin";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Create from "./views/Create.tsx";
import { Account } from "./views/Account";
import { Toaster } from "./components/ui/toaster";
import { UpdatePersonaDetails } from "./views/manage_personas/UpdatePersonaDetails";
import { PersonaView } from "./views/manage_personas/PersonaView";
import AutonomousAgents from "./views/AutonomousAgents.tsx";
import { usePageTracking } from "./components/GoogleAnalytics";
import ContentManagement from "./views/ContentManagement.tsx";
import MintTokenPage from "./views/MintTokenPage";
import ViewTokenPage from "./views/ViewTokenPage";
import { PrivyProvider, usePrivy } from "@privy-io/react-auth";
import { toSolanaWalletConnectors } from "@privy-io/react-auth/solana";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CheckForUpdatesToast } from "@/components/CheckForUpdatesToast";
import * as Sentry from "@sentry/react";
import { ErrorElement } from "./components/ErrorElement.tsx";
import UnauthorizedPage from "./views/UnauthorizedPage.tsx";
import { AgentsPage } from "./views/manage_personas/AgentsPage.tsx";
import { CreatePersonaFlow } from "./components/createPersona/CreatePersonaFlow.tsx";
import { useAuth } from "@/hooks/use-auth.tsx";
import PrivatePreviewRoute from "./components/PrivatePreviewRoute.tsx";

import { TelegramConsentPage } from "@/components/mock/TelegramConsentPage"; // Adjust path if needed

// PageTracker component that uses page tracking inside the Router context
function PageTracker() {
  usePageTracking(); // Calls usePageTracking only within Router context
  return null; // This component only serves to trigger page tracking
}

const solanaConnectors = toSolanaWalletConnectors({
  // By default, shouldAutoConnect is enabled
  shouldAutoConnect: true,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // By default, all queries are cached for 5 minutes
      staleTime: 5 * 60 * 1000,
    },
  },
});

Sentry.init({
  dsn: "https://022ed5cc7e3aebaaec1cb6941e0da1bb@o4507095650992129.ingest.de.sentry.io/4508637411999824",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const handleSupportClick = () => {
  window.open("https://forms.gle/Erp9LaBxJRHYybYB7", "_blank");
};

const router = createBrowserRouter([
  { path: "*", element: <Root />, errorElement: <ErrorElement /> },
]);

function App() {
  return (
    <PrivyProvider
      appId={import.meta.env.VITE_PRIVY_APP_ID}
      config={{
        externalWallets: {
          solana: {
            connectors: solanaConnectors,
          },
        },
      }}
    >
      <AuthorizationProvider>
        <ReactQueryProvider>
          <TooltipProvider>
            <RouterProvider router={router} />
          </TooltipProvider>
        </ReactQueryProvider>
      </AuthorizationProvider>
    </PrivyProvider>
  );
}

function Root() {
  return (
    <>
      <ScrollRestoration />
      <PageTracker /> {/* Ensure PageTracker is a child of Router */}
      <CheckForUpdatesToast />
      <div
        className="flex flex-col lg:grid min-h-screen max-w-[100vw] *:max-w-full *:overflow-auto"
        style={{
          gridTemplateColumns:
            "var(--navbar-width) calc(100% - var(--navbar-width))",
        }}
      >
        <Navbar />
        <Routes>
          <Route
            path="/content-reviewer"
            element={
              <PrivateRoute>
                <ContentReviewer />
              </PrivateRoute>
            }
          />
          <Route
            path="/content-management"
            element={
              <PrivatePreviewRoute feature="reply-mentions">
                <ContentManagement />
              </PrivatePreviewRoute>
            }
          />
          <Route
            path="/marketplace"
            element={
              <RestrictedRoute>
                <TokenAgentsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/agents/:personaId"
            element={
              <RestrictedRoute>
                <PersonaView />
              </RestrictedRoute>
            }
          />
          <Route
            path="/agents"
            element={
              <RestrictedRoute>
                <AgentsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/agents/new"
            element={
              <PrivateRoute>
                <CreatePersonaFlow />
              </PrivateRoute>
            }
          />
          <Route
            path="/agents/:personaId/edit"
            element={
              <PrivateRoute>
                <UpdatePersonaDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/"
            element={
              <RestrictedRoute>
                <AgentsPage />
              </RestrictedRoute>
            }
          />
          <Route path="/consent" element={<TelegramConsentPage />} />

          {/* Protecting Admin Route */}
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/create"
            element={
              <PrivateRoute>
                <Create />
              </PrivateRoute>
            }
          />

          <Route
            path="/create/:tab"
            element={
              <PrivateRoute>
                <Create />
              </PrivateRoute>
            }
          />

          <Route
            path="/autonomous"
            element={
              <PrivateRoute>
                <AutonomousAgents />
              </PrivateRoute>
            }
          />
          <Route
            path="/agents/:personaId/token"
            element={
              <PrivateRoute>
                <MintTokenPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/agents/:personaId/view-token"
            element={
              <PrivateRoute>
                <ViewTokenPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/account"
            element={
              <PrivateRoute>
                <Account />
              </PrivateRoute>
            }
          />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      <Button
        className="fixed right-5 bottom-5 bg-primary text-black z-[1000] rounded-full p-2 h-auto"
        onClick={handleSupportClick}
      >
        <Bug className="h-8 w-8 p-0.5" />
      </Button>
      <Toaster />
    </>
  );
}

// AdminRoute component to protect admin route based on roles
function AdminRoute({ children }: PropsWithChildren) {
  const { hasAtLeastRole } = useAuth();

  // Check if user has the roles super_admin or tenant_admin
  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  if (!isAdmin) {
    // Redirect to Unauthorized page if the user is not an admin
    return <Navigate to="/unauthorized" />;
  }

  // Render children if the user has admin access
  return children;
}

function ReactQueryProvider({ children }: PropsWithChildren) {
  const { authenticated, ready } = usePrivy();
  const prevIsAuthenticated = useRef(authenticated);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (prevIsAuthenticated.current !== authenticated) {
      if (!authenticated) {
        // Clear cache when authentication status changes.
        // This is important to avoid keeping user's data in cache after they log out.
        queryClient.clear();
      }
    }

    prevIsAuthenticated.current = authenticated;
  }, [authenticated]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

export default App;
